// Achievement system: lifetime progress in localStorage, toast popups.

const KEY = 'bb-achievements';
const STATS_KEY = 'bb-stats';
const STREAK_KEY = 'bb-streak';

export interface Achievement {
  id: string;
  name: string;
  emoji: string;
  desc: string;
}

export const ACHIEVEMENTS: Achievement[] = [
  { id: 'first_win',        name: 'First Blood',         emoji: '🏆', desc: 'Win your first battle' },
  { id: 'kills_25',         name: 'Slayer',              emoji: '⚔️', desc: 'Score 25 lifetime kills' },
  { id: 'kills_100',        name: 'Centurion',           emoji: '💯', desc: 'Score 100 lifetime kills' },
  { id: 'flawless',         name: 'Flawless Victory',    emoji: '✨', desc: 'Win without losing a single unit' },
  { id: 'comeback',         name: 'Comeback Kid',        emoji: '🔥', desc: 'Win with only one unit left' },
  { id: 'streak_3',         name: 'Hot Streak',          emoji: '🌶️', desc: 'Win 3 battles in a row' },
  { id: 'streak_10',        name: 'Unstoppable',         emoji: '👑', desc: 'Win 10 battles in a row' },
  { id: 'crit_5',           name: 'Critical Mind',       emoji: '🎯', desc: 'Land 5 crits in one battle' },
  { id: 'ult_3',            name: 'Ultimate Power',      emoji: '⭐', desc: 'Cast 3 ultimates in one battle' },
  { id: 'plant_only',       name: 'Garden Defender',     emoji: '🌱', desc: 'Win with an all-Plant team' },
  { id: 'zombie_only',      name: 'Brain Eater',         emoji: '🧟', desc: 'Win with an all-Zombie team' },
  { id: 'pokemon_only',     name: 'Gotta Catch Em All',  emoji: '⚡', desc: 'Win with an all-Pokémon team' },
  { id: 'mixed',            name: 'Crossover Crisis',    emoji: '🌀', desc: 'Win with all 3 factions on one team' },
  { id: 'legendary',        name: 'Chosen One',          emoji: '🌟', desc: 'Win using Mewtwo, Arceus, or Rayquaza' },
];

interface Unlocked { ids: Record<string, number>; }
interface CharStats { wins: number; kills: number; dmg: number; }
interface Stats { chars: Record<string, CharStats>; lifetimeKills: number; }

function loadUnlocked(): Unlocked {
  try { return JSON.parse(localStorage.getItem(KEY) || '{"ids":{}}'); } catch { return { ids: {} }; }
}
function saveUnlocked(u: Unlocked) { localStorage.setItem(KEY, JSON.stringify(u)); }

export function loadStats(): Stats {
  try { return JSON.parse(localStorage.getItem(STATS_KEY) || '{"chars":{},"lifetimeKills":0}'); }
  catch { return { chars: {}, lifetimeKills: 0 }; }
}
function saveStats(s: Stats) { localStorage.setItem(STATS_KEY, JSON.stringify(s)); }

export function getStreak(): number { return parseInt(localStorage.getItem(STREAK_KEY) || '0', 10); }
function setStreak(n: number) { localStorage.setItem(STREAK_KEY, String(n)); }

export function recordCharResult(charId: string, won: boolean, kills: number, dmg: number) {
  const s = loadStats();
  const c = s.chars[charId] ?? { wins: 0, kills: 0, dmg: 0 };
  if (won) c.wins += 1;
  c.kills += kills;
  c.dmg += dmg;
  s.chars[charId] = c;
  s.lifetimeKills += kills;
  saveStats(s);
}

export function getCharStats(charId: string): CharStats {
  return loadStats().chars[charId] ?? { wins: 0, kills: 0, dmg: 0 };
}

export interface MatchSummary {
  won: boolean;
  survivors: number;
  factions: string[]; // ['pokemon','plant','zombie']
  hadLegendary: boolean;
  critsLanded: number;
  ultsCast: number;
}

export function checkAchievements(m: MatchSummary): Achievement[] {
  const u = loadUnlocked();
  const s = loadStats();
  const newly: Achievement[] = [];
  const unlock = (id: string) => {
    if (u.ids[id]) return;
    u.ids[id] = Date.now();
    const a = ACHIEVEMENTS.find(x => x.id === id);
    if (a) newly.push(a);
  };

  if (m.won) unlock('first_win');
  if (s.lifetimeKills >= 25) unlock('kills_25');
  if (s.lifetimeKills >= 100) unlock('kills_100');
  if (m.won && m.survivors > 0 && s.chars && Object.keys(s.chars).length > 0) {
    // flawless = survivors == starting team size — caller knows; pass via factions trick instead
  }
  if (m.won && m.factions.length === 1 && m.factions[0] === 'plant') unlock('plant_only');
  if (m.won && m.factions.length === 1 && m.factions[0] === 'zombie') unlock('zombie_only');
  if (m.won && m.factions.length === 1 && m.factions[0] === 'pokemon') unlock('pokemon_only');
  if (m.won && m.factions.length === 3) unlock('mixed');
  if (m.won && m.hadLegendary) unlock('legendary');
  if (m.critsLanded >= 5) unlock('crit_5');
  if (m.ultsCast >= 3) unlock('ult_3');

  // streak handled separately
  saveUnlocked(u);
  return newly;
}

export function recordWinStreak(won: boolean): number {
  let s = getStreak();
  if (won) s += 1; else s = 0;
  setStreak(s);
  const u = loadUnlocked();
  if (s >= 3 && !u.ids['streak_3']) {
    u.ids['streak_3'] = Date.now();
    saveUnlocked(u);
  }
  if (s >= 10 && !u.ids['streak_10']) {
    u.ids['streak_10'] = Date.now();
    saveUnlocked(u);
  }
  return s;
}

export function checkFlawless(survivors: number, teamSize: number) {
  if (survivors !== teamSize) return null;
  const u = loadUnlocked();
  if (u.ids['flawless']) return null;
  u.ids['flawless'] = Date.now();
  saveUnlocked(u);
  return ACHIEVEMENTS.find(a => a.id === 'flawless') || null;
}

export function checkComeback(survivors: number, won: boolean) {
  if (!won || survivors !== 1) return null;
  const u = loadUnlocked();
  if (u.ids['comeback']) return null;
  u.ids['comeback'] = Date.now();
  saveUnlocked(u);
  return ACHIEVEMENTS.find(a => a.id === 'comeback') || null;
}

export function showToast(a: Achievement, root: HTMLElement) {
  const t = document.createElement('div');
  t.className = 'achievement-toast';
  t.innerHTML = `
    <div class="ach-emoji">${a.emoji}</div>
    <div class="ach-body">
      <div class="ach-title">Achievement Unlocked!</div>
      <div class="ach-name">${a.name}</div>
      <div class="ach-desc">${a.desc}</div>
    </div>
  `;
  root.appendChild(t);
  setTimeout(() => t.remove(), 4500);
}
